import type {
  DeleteFavoriteRequest,
  DeleteFavoriteResponse,
  FavoriteItem,
  FavoritePageDescriptor,
  FavoritesResponse,
  UpsertFavoriteRequest,
  UpsertFavoriteResponse,
} from '@/types/favorites';
import { baseNavigationItems } from '@/ui/Layout/Sidebar/NavigationItems/navigationItems';
import { buildFavoritePageDescriptor } from '@/utils/favorites/navigation';
import { normalizeFavoritePath } from '@/utils/favorites/path';
import useDelete from '@/utils/hooks/api/typescript/useDelete';
import useGet from '@/utils/hooks/api/typescript/useGet';
import usePut from '@/utils/hooks/api/typescript/usePut';
import useSession from '@/utils/hooks/useSession';
import { usePageMetadata } from '@/utils/pageMetadata/PageMetadataContext';
import { useQueryClient } from '@tanstack/react-query';
import useHubTranslation from '@/utils/i18n/useHubTranslation';
import { isPermissionedRoute } from '@/utils/routing/routing';
import { useRouter } from 'next/router';
import {
  useCallback,
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';

interface FavoritesContextValue {
  favorites: FavoriteItem[];
  currentPage: FavoritePageDescriptor | null;
  isFavorite: boolean;
  isLoading: boolean;
  isMutating: boolean;
  toggleCurrentPage: () => Promise<void>;
  syncFavorites: (favorites: FavoriteItem[]) => void;
}

const defaultContextValue: FavoritesContextValue = {
  favorites: [],
  currentPage: null,
  isFavorite: false,
  isLoading: false,
  isMutating: false,
  toggleCurrentPage: async () => undefined,
  syncFavorites: () => undefined,
};

const FavoritesContext =
  createContext<FavoritesContextValue>(defaultContextValue);

const FAVORITES_QUERY_KEY = '/api/favorites';
const FAVORITES_WITH_INACCESSIBLE_QUERY_KEY =
  '/api/favorites?includeInaccessible=true';

function sortFavorites(items: FavoriteItem[]): FavoriteItem[] {
  return [...items].sort((left, right) => left.sortOrder - right.sortOrder);
}

function upsertFavorite(
  items: FavoriteItem[],
  nextFavorite: FavoriteItem
): FavoriteItem[] {
  const favoritesWithoutMatch = items.filter(
    (favorite) =>
      favorite.id !== nextFavorite.id && favorite.path !== nextFavorite.path
  );

  return sortFavorites([...favoritesWithoutMatch, nextFavorite]);
}

export function FavoritesProvider({
  children,
}: PropsWithChildren): ReactElement {
  const router = useRouter();
  const session = useSession();
  const queryClient = useQueryClient();
  const { translateHubUi } = useHubTranslation();
  const { pageMetadata } = usePageMetadata();
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);

  const pageMetadataForRoute =
    pageMetadata?.routeKey === router.pathname ? pageMetadata : null;

  const { data: favoritesResponse, isLoading } = useGet<FavoritesResponse>(
    '/favorites',
    {
      fireOnMount: true,
      keepPreviousData: true,
    }
  );

  const { fireAsync: saveFavoriteRequest, isLoading: isSavingFavorite } =
    usePut<UpsertFavoriteResponse, UpsertFavoriteRequest>('/favorites', {
      successMessage: translateHubUi(
        'nav.favorites.addSuccess',
        'Added to Favorites'
      ),
      errorMessage: translateHubUi(
        'nav.favorites.addError',
        'Failed to add to Favorites'
      ),
    });

  const { fireAsync: removeFavoriteRequest, isLoading: isRemovingFavorite } =
    useDelete<DeleteFavoriteResponse, DeleteFavoriteRequest>('/favorites', {
      successMessage: translateHubUi(
        'nav.favorites.removeSuccess',
        'Removed from Favorites'
      ),
      errorMessage: translateHubUi(
        'nav.favorites.removeError',
        'Failed to remove from Favorites'
      ),
    });

  const syncFavorites = useCallback(
    (nextFavorites: FavoriteItem[]) => {
      const allFavorites = sortFavorites(nextFavorites);
      const accessibleFavorites =
        session?.user && session?.org
          ? allFavorites.filter((favorite) =>
              isPermissionedRoute(favorite.pathname, session.user, session.org)
            )
          : allFavorites;

      queryClient.setQueryData([FAVORITES_QUERY_KEY], {
        data: {
          favorites: accessibleFavorites,
        },
      });
      queryClient.setQueryData([FAVORITES_WITH_INACCESSIBLE_QUERY_KEY], {
        data: {
          favorites: allFavorites,
        },
      });

      setFavorites(accessibleFavorites);
    },
    [queryClient, session?.org, session?.user]
  );

  useEffect(() => {
    if (!favoritesResponse?.favorites) {
      return;
    }

    syncFavorites(favoritesResponse.favorites);
  }, [favoritesResponse, syncFavorites]);

  const currentPage = useMemo(() => {
    try {
      return buildFavoritePageDescriptor({
        path: router.asPath,
        navigationItems: baseNavigationItems,
        pageTitle: pageMetadataForRoute?.title,
        crumbLabels: pageMetadataForRoute?.crumbLabels,
        favoriteMetadata: pageMetadataForRoute?.favoriteMetadata,
      });
    } catch (error) {
      console.error('Unable to derive favorite metadata for current route.', {
        error,
      });
      return null;
    }
  }, [pageMetadataForRoute, router.asPath]);

  const isFavorite = useMemo(() => {
    if (!currentPage) {
      return false;
    }

    return favorites.some(
      (favorite) => favorite.path === normalizeFavoritePath(currentPage.path)
    );
  }, [currentPage, favorites]);

  async function addCurrentPage(): Promise<void> {
    if (!currentPage) {
      return;
    }

    const response = await saveFavoriteRequest({
      path: currentPage.path,
      displayLabel: currentPage.displayLabel,
      fullLabel: currentPage.fullLabel,
      source: currentPage.source,
    });

    const now = new Date().toISOString();
    const favorite = response?.data?.favorite || {
      id: normalizeFavoritePath(currentPage.path),
      ...currentPage,
      sortOrder:
        favorites.reduce(
          (highestSortOrder, existingFavorite) =>
            Math.max(highestSortOrder, existingFavorite.sortOrder),
          0
        ) + 1,
      createdAt: now,
      updatedAt: now,
    };

    syncFavorites(upsertFavorite(favorites, favorite));
  }

  async function removeCurrentPage(): Promise<void> {
    if (!currentPage) {
      return;
    }

    const normalizedPath = normalizeFavoritePath(currentPage.path);
    const currentFavorite = favorites.find(
      (favorite) => favorite.path === normalizedPath
    );
    const response = await removeFavoriteRequest(
      currentFavorite
        ? { favoriteId: currentFavorite.id }
        : { path: normalizedPath }
    );
    const removedFavoriteId =
      response?.data?.favorite?.id || currentFavorite?.id || normalizedPath;

    syncFavorites(
      favorites.filter(
        (favorite) =>
          favorite.id !== removedFavoriteId && favorite.path !== normalizedPath
      )
    );
  }

  async function toggleCurrentPage(): Promise<void> {
    if (!currentPage) {
      return;
    }

    if (isFavorite) {
      await removeCurrentPage();
      return;
    }

    await addCurrentPage();
  }

  const value = useMemo(
    () => ({
      favorites,
      currentPage,
      isFavorite,
      isLoading,
      isMutating: isSavingFavorite || isRemovingFavorite,
      toggleCurrentPage,
      syncFavorites,
    }),
    [
      currentPage,
      favorites,
      isFavorite,
      isLoading,
      isRemovingFavorite,
      isSavingFavorite,
      syncFavorites,
    ]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites(): FavoritesContextValue {
  return useContext(FavoritesContext);
}
